import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Pencil, X, Check } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import type { Tool } from '@/types/database';

const ENGINE_OPTIONS = ['internal', 'native_llm', 'pipeline'] as const;
const CATEGORY_OPTIONS = [
  'Knowledge',
  'Meetings',
  'Monitoring',
  'Help',
  'Other',
];
const STATUS_OPTIONS = ['active', 'disabled'] as const;
const SCOPE_OPTIONS = ['platform', 'org'] as const;

interface Skill {
  id: string;
  name: string;
  version: number;
}

type EngineOption = (typeof ENGINE_OPTIONS)[number];
type StatusOption = (typeof STATUS_OPTIONS)[number];
type ScopeOption = (typeof SCOPE_OPTIONS)[number];

interface FormState {
  name: string;
  slug: string;
  description: string;
  engine: EngineOption;
  category: string;
  status: StatusOption;
  scope: ScopeOption;
  skill_id: string;
  config: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  slug: '',
  description: '',
  engine: 'internal',
  category: 'Knowledge',
  status: 'active',
  scope: 'platform',
  skill_id: '',
  config: '{}',
};

interface FormPanelProps {
  editingId: string | null;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  skills: Skill[];
  error: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const FormPanel: React.FC<FormPanelProps> = ({
  editingId,
  form,
  setForm,
  skills,
  error,
  saving,
  onSave,
  onCancel,
}) => (
  <Card className="mb-6">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-base">
          {editingId ? 'Edit Tool' : 'New Tool'}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Deep Research Brief"
          />
        </div>
        <div className="space-y-1">
          <Label>Slug *</Label>
          <Input
            value={form.slug}
            onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
            placeholder="deep-research-brief"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label>Description *</Label>
        <Textarea
          rows={2}
          value={form.description}
          onChange={(e) =>
            setForm((p) => ({ ...p, description: e.target.value }))
          }
          placeholder="Produces a detailed research brief…"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Engine *</Label>
          <Select
            value={form.engine}
            onValueChange={(v) =>
              setForm((p) => ({ ...p, engine: v as EngineOption }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENGINE_OPTIONS.map((e) => (
                <SelectItem key={e} value={e}>
                  {e}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Skill (if engine=native_llm)</Label>
          <Select
            value={form.skill_id}
            onValueChange={(v) => setForm((p) => ({ ...p, skill_id: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="— none —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">— none —</SelectItem>
              {skills.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} (v{s.version})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Category</Label>
          <Select
            value={form.category}
            onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) =>
              setForm((p) => ({ ...p, status: v as StatusOption }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label>Scope</Label>
        <Select
          value={form.scope}
          onValueChange={(v) =>
            setForm((p) => ({ ...p, scope: v as ScopeOption }))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCOPE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Config (JSON)</Label>
        <Textarea
          rows={4}
          className="font-mono text-xs"
          value={form.config}
          onChange={(e) => setForm((p) => ({ ...p, config: e.target.value }))}
          placeholder='{ "key": "value" }'
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button onClick={onSave} disabled={saving}>
          <Check className="w-4 h-4 mr-1" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const ToolRegistry: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        apiFetch('/api/tools'),
        apiFetch('/api/skills'),
      ]);
      const tData = await tRes.json();
      const sData = await sRes.json();
      setTools(tData?.data ?? tData ?? []);
      setSkills(sData?.data ?? []);
    } catch {
      /* silent */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowCreate(true);
    setError(null);
  };
  const openEdit = (t: Tool) => {
    setForm({
      name: t.name,
      slug: t.slug,
      description: t.description ?? '',
      engine: (t.engine ?? 'internal') as (typeof EMPTY_FORM)['engine'],
      category: t.category ?? 'Knowledge',
      status: (t.status ?? 'active') as (typeof EMPTY_FORM)['status'],
      scope: (t.scope ?? 'platform') as (typeof EMPTY_FORM)['scope'],
      skill_id: t.skill_id ?? '',
      config: JSON.stringify(t.config ?? {}, null, 2),
    });
    setEditingId(t.id);
    setShowCreate(false);
    setError(null);
  };

  const slugify = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const save = async () => {
    setError(null);
    let configParsed: Record<string, unknown> = {};
    try {
      configParsed = JSON.parse(form.config || '{}');
    } catch {
      setError('Config must be valid JSON');
      return;
    }

    setSaving(true);
    try {
      const url = editingId ? `/api/tools/${editingId}` : '/api/tools';
      const method = editingId ? 'PATCH' : 'POST';
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          ...form,
          slug: form.slug || slugify(form.name),
          skill_id: form.skill_id || null,
          config: configParsed,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error ?? 'Save failed');
        return;
      }
      setShowCreate(false);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Tool Registry</h2>
        <Button size="sm" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" />
          New Tool
        </Button>
      </div>

      {(showCreate || editingId) && (
        <FormPanel
          editingId={editingId}
          form={form}
          setForm={setForm}
          skills={skills}
          error={error}
          saving={saving}
          onSave={save}
          onCancel={() => {
            setShowCreate(false);
            setEditingId(null);
          }}
        />
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Engine</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tools.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-gray-400 py-8"
                >
                  No tools yet. Create one above.
                </TableCell>
              </TableRow>
            )}
            {tools.map((t) => (
              <TableRow
                key={t.id}
                className={editingId === t.id ? 'bg-blue-50' : ''}
              >
                <TableCell className="font-medium">
                  {t.name}
                  <br />
                  <span className="text-xs text-gray-400">{t.slug}</span>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{t.engine ?? t.type}</Badge>
                </TableCell>
                <TableCell>{t.category ?? '—'}</TableCell>
                <TableCell>{t.scope ?? 'platform'}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      t.status === 'disabled' ? 'destructive' : 'default'
                    }
                  >
                    {t.status ?? 'active'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(t)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
